import * as React from "react";
import {
    DataGridPro,
    GridColDef,
    GridColumnGroupingModel,
    GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { Box, Tooltip, Typography } from "@mui/material";
import SelfStatusCell, { SelfStatusCellProps } from "../selfStatusCell";
import ScoringStatusCell from "../scoreStatusCell";
import RaterStatusCell from "../raterStatusCell";
import AssessmentStatusGroupHeader from "../assessmentStatusGroupHeader";
import { ParticipantInstrumentStatus } from "../../../../../../../services/types/enterpriseParticipantApiTypes";

export interface SingleAssessmentStatusDataGridNewProps {
    assessment: ParticipantInstrumentStatus;
    isLoading: boolean;
}

const SingleAssessmentStatusDataGridNew: React.FC<SingleAssessmentStatusDataGridNewProps> = (
    props
) => {
    const raterColWidthWide = 100;
    const raterColWidthNarrow = 80;
    const selfAndScoreColWidth = 114;

    const getScorability = (): number | undefined => {
        const paxInstrument = props.assessment;
        if (paxInstrument == null) return 0;
        if (paxInstrument.selfAssessmentStatusTypeId === 5) return 5;
        if (paxInstrument.scoringStatus === 2) return 2;
        if (paxInstrument.reportQualityTypeId === 4) return 1;
        return 0;
    };

    const getSelfStatusParams = (): SelfStatusCellProps => {
        const paxInstrument = props.assessment;
        const st = paxInstrument?.selfAssessmentStatusTypeId ?? 0;
        const numRaters = paxInstrument?.supportedRaterTypeCount ?? 0;
        const scrd =
            paxInstrument?.scoringStatus == null ? false : paxInstrument.scoringStatus === 2;
        return { status: st, hasRaters: numRaters > 1, scored: scrd };
    };

    let colDefs: GridColDef[] = [
        {
            field: `${props.assessment.instrumentId}.scorable`,
            headerAlign: "center",
            headerName: "Scorability",
            headerClassName: "superHeader",
            cellClassName: "whiteCell",
            type: "string",
            renderCell: (params: GridRenderCellParams) => (
                <ScoringStatusCell status={getScorability() ?? 0} />
            ),
            flex: 1,
            align: "center",
            // maxWidth: selfAndScoreColWidth,
            minWidth: selfAndScoreColWidth,
            disableColumnMenu: true,
            sortable: false,
        },
        {
            field: `${props.assessment.instrumentId}.self`,
            headerAlign: "center",
            headerName: "Self",
            headerClassName: "superHeader",
            cellClassName: "whiteCell",
            type: "string",
            renderCell: (params: GridRenderCellParams) => (
                <SelfStatusCell {...getSelfStatusParams()} />
            ),
            flex: 1,
            align: "center",
            // maxWidth: selfAndScoreColWidth,
            minWidth: selfAndScoreColWidth,
            disableColumnMenu: true,
            sortable: false,
        },
    ];

    const raters = props.assessment?.raters.filter((f) => f.raterTypeId !== 1) ?? [];

    if (raters.length > 0) {
        raters.forEach((r) => {
            colDefs.push({
                field: `${props.assessment.instrumentId}.${r.raterTypeName?.toLowerCase()}`,
                headerAlign: "center",
                renderHeader: () => {
                    return (
                        <Tooltip
                            title={
                                <div>
                                    <Typography variant="body2">
                                        Minimum: {r.minimumRequired}
                                    </Typography>
                                    <Typography variant="body2">
                                        Recommended: {r.minimumRecommended}
                                    </Typography>
                                </div>
                            }
                        >
                            <div
                                style={{
                                    lineHeight: "1.5em",
                                }}
                            >
                                <span
                                    style={{
                                        justifyContent: "center",
                                        display: "flex",
                                    }}
                                >
                                    {r?.raterTypeName}
                                </span>
                                <span
                                    style={{
                                        justifyContent: "center",
                                        display: "flex",
                                    }}
                                >{`${r.minimumRequired}/${r.minimumRecommended}`}</span>
                            </div>
                        </Tooltip>
                    );
                },
                headerClassName: "greyCellHeader",
                // align: "left",
                align: "center",
                cellClassName: "greyCell",
                type: "string",
                renderCell: (params: GridRenderCellParams) => (
                    <RaterStatusCell
                        assessmentNew={props.assessment}
                        instrumentId={props.assessment.instrumentId}
                        raterId={r.raterTypeId}
                        isWide={r?.raterTypeName.length > 10}
                    />
                ),
                flex: 1,
                minWidth: r?.raterTypeName.length > 10 ? raterColWidthWide : raterColWidthNarrow,
                // maxWidth: r?.raterTypeName.length > 10 ? raterColWidthWide : raterColWidthNarrow,
                disableColumnMenu: true,
                sortable: false,
            });
        });
    }

    const colGroupingModel: GridColumnGroupingModel = [
        {
            groupId: props.assessment.shortName,
            children: colDefs.map((col: GridColDef) => ({ field: col.field })),
            headerAlign: "center",
            headerClassName: "groupedHeader",
            renderHeaderGroup: (params) => {
                return (
                    <AssessmentStatusGroupHeader
                        {...params}
                        headerText={props.assessment.name}
                        hoverTitle={`${props.assessment.name} (${props.assessment.shortName})`}
                    />
                );
            },
        },
    ];

    return (
        <Box sx={{ maxWidth: "fitContent", minWidth: "fitContent", overflow: "hidden" }}>
            <DataGridPro
                getRowId={(row) => row.instrumentId}
                experimentalFeatures={{ columnGrouping: true }}
                loading={props.isLoading}
                columnGroupingModel={colGroupingModel}
                columns={colDefs}
                rows={[props.assessment]}
                disableSelectionOnClick={true}
                disableExtendRowFullWidth={true}
                hideFooter={true}
                sx={{
                    "& .MuiDataGrid-columnSeparator": {
                        visibility: "hidden",
                    },
                    "& .greyCell": {
                        backgroundColor: "#F0F1F3",
                        paddingLeft: "2px",
                    },
                    "& .greyCellHeader": {
                        backgroundColor: "#F0F1F3",
                        borderLeft: "1px solid #E0E0E0",
                        paddingLeft: "2px",
                        paddingRight: "2px",
                        fontWeight: "bold",
                    },
                    "& .superHeader": {
                        borderLeft: "1px solid #E0E0E0",
                        fontWeight: "bold",
                    },
                    "& .groupedHeader": {
                        // backgroundColor: "#ebf3f6",
                        borderLeft: "1px solid #E0E0E0",
                        fontWeight: "bold",
                    },
                    "& .whiteCell": {
                        paddingLeft: "4px",
                    },
                }}
                autoHeight
            />
        </Box>
    );
};

export default SingleAssessmentStatusDataGridNew;
